import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './day-settlement-report.css';

const DaySettlementReport = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReport();
    }, [date]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${PRODUCTION_URL}/api/admin/reports/day-settlement?date=${date}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setReport(data.data);
            }
        } catch (err) {
            console.error('Error fetching report:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const handlePrint = () => {
        window.print();
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-IN', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <AdminLayout>
            <div className="settlement-container">
                <div className="settlement-header">
                    <div className="header-left">
                        <button onClick={() => navigate(-1)} className="btn-back">← Back</button>
                        <h1>Day Settlement Report</h1>
                    </div>
                    <div className="header-right">
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="date-picker"
                        />
                        <button onClick={handlePrint} className="btn btn-secondary">
                            🖨️ Print
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="loading">Loading report...</div>
                ) : report ? (
                    <div className="settlement-content">
                        <div className="report-date">
                            <h2>{formatDate(report.date)}</h2>
                        </div>

                        {/* Revenue Summary */}
                        <div className="report-section">
                            <h3>Revenue Summary</h3>
                            <div className="revenue-cards">
                                <div className="revenue-card room">
                                    <span className="label">Room Revenue</span>
                                    <span className="value">{formatCurrency(report.revenue.room)}</span>
                                </div>
                                <div className="revenue-card food">
                                    <span className="label">Food & Beverages</span>
                                    <span className="value">{formatCurrency(report.revenue.food)}</span>
                                </div>
                                <div className="revenue-card bar">
                                    <span className="label">Bar Revenue</span>
                                    <span className="value">{formatCurrency(report.revenue.bar)}</span>
                                </div>
                                <div className="revenue-card other">
                                    <span className="label">Other Charges</span>
                                    <span className="value">{formatCurrency(report.revenue.guestBills)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Guest Bills Breakdown */}
                        {report.revenue.guestBillsByType && report.revenue.guestBillsByType.length > 0 && (
                            <div className="report-section">
                                <h3>Additional Charges Breakdown</h3>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Count</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.revenue.guestBillsByType.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="capitalize">{item.bill_type}</td>
                                                <td>{item.count}</td>
                                                <td>{formatCurrency(item.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Gross & Net */}
                        <div className="report-section totals">
                            <div className="total-row">
                                <span>Gross Revenue</span>
                                <span className="gross">{formatCurrency(report.revenue.gross)}</span>
                            </div>
                            <div className="total-row refund">
                                <span>Total Refunds ({report.refunds.count})</span>
                                <span>- {formatCurrency(report.refunds.total)}</span>
                            </div>
                            <div className="total-row net">
                                <span>Net Settlement</span>
                                <span>{formatCurrency(report.netSettlement)}</span>
                            </div>
                        </div>

                        {/* Payment Methods */}
                        <div className="report-section">
                            <h3>Payment Method Breakdown</h3>
                            <div className="payment-methods">
                                {report.paymentMethods && report.paymentMethods.length > 0 ? (
                                    report.paymentMethods.map((method, idx) => (
                                        <div key={idx} className="payment-method-item">
                                            <div className="method-info">
                                                <span className="method-name capitalize">{method.payment_method || 'Unknown'}</span>
                                                <span className="method-count">{method.count} transactions</span>
                                            </div>
                                            <span className="method-amount">{formatCurrency(method.total)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-data">No transactions for this date</p>
                                )}
                            </div>
                        </div>

                        {/* Operations */}
                        <div className="report-section">
                            <h3>Operations Summary</h3>
                            <div className="operations-grid">
                                <div className="operation-item">
                                    <span className="op-icon">🏨</span>
                                    <span className="op-value">{report.operations.checkIns}</span>
                                    <span className="op-label">Check-ins</span>
                                </div>
                                <div className="operation-item">
                                    <span className="op-icon">🚪</span>
                                    <span className="op-value">{report.operations.checkOuts}</span>
                                    <span className="op-label">Check-outs</span>
                                </div>
                                <div className="operation-item">
                                    <span className="op-icon">💳</span>
                                    <span className="op-value">{report.operations.roomTransactions}</span>
                                    <span className="op-label">Room Payments</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="no-data">No data available for this date</div>
                )}
            </div>
        </AdminLayout>
    );
};

export default DaySettlementReport;
